import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ProfitLossChart from "./ProfitLossChart";
import DataBreakdown from "./DataBreakdown";
import plApi from "../../api/plApi";
import accountApi from "../../api/accountApi";
import { parseExcel } from "../../utils/parseExcel";
import ForecastChart from "./ProfitAndLossForecastGraph";

const ProfitAndLoss = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectForecastMonth, setSelectForecastMonth] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("monthWise");

  const [forecastData, setForecastData] = useState(null);
  const [showForecast, setShowForecast] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState("2025-01-01");
  const [endDate, setEndDate] = useState(today);

  const token = useSelector((state) => state.auth.access_token);

  useEffect(() => {
    if (token) {
      accountApi.getAllAccounts(token, 0, 50).then((res) => {
        const list = res?.data?.content || [];
        setAccounts(list);
        if (list.length > 0) setSelectedAccountId(list[0].id);
      });
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    if (!selectedAccountId || !token) return;

    setLoading(true);
    try {
      let res;
      if (filterType === "monthWise") {
        res = await plApi.getProfiteAndLossMonthWise(
          token,
          selectedAccountId,
          startDate,
          endDate,
        );
      } else {
        res = await plApi.getProfiteAndLossOneMonth(
          token,
          selectedAccountId,
          startDate,
        );
      }
      setReportData(res?.data || res);
    } catch (err) {
      console.error(err);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, filterType, startDate, endDate, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStartDateChange = (e) => {
    const newStart = e.target.value;

    if (newStart > today) {
      toast.warning("Start date cannot be in the future");
      return;
    }

    setStartDate(newStart);

    if (endDate && endDate < newStart) {
      toast.warning("End date cannot be earlier than start date");
    }
  };

  const handleForecastReport = async () => {
    if (!selectedAccountId) {
      toast.warning("Please select an account");
      return;
    }

    try {
      setLoading(true);
      setShowForecast(false);

      const res = await plApi.getProfiteAndLossFOrecastReport(
        selectedAccountId,
        selectForecastMonth, // ✅ dynamic value
        token,
      );

      setForecastData(res?.data || res);
      setShowForecast(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load forecast report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showForecast) {
      handleForecastReport();
    }
  }, [selectForecastMonth]);

  const handleEndDateChange = (e) => {
    const newEnd = e.target.value;

    if (newEnd < startDate) {
      toast.warning("End date cannot be earlier than start date");
      return;
    }

    setEndDate(newEnd);
  };

  const handleUploadSuccess = () => {
    setIsModalOpen(false);
    setJsonData(null);
    setFile(null);
    fetchData();
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Financial Insights</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-primary text-white px-5 py-2 rounded-lg font-medium shadow transition"
        >
          Upload Report
        </button>
      </div>

      <div className="max-w-7xl mx-auto bg-white p-4 rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-2">
        <div>
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Account
          </label>
          <select
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            className="w-full bg-gray-50 rounded-lg text-sm font-semibold px-3 py-2 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select Account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.accountName || a.companyName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            Analysis Mode
          </label>
          <div className="flex bg-gray-50 p-1 rounded-lg">
            <button
              onClick={() => setFilterType("monthWise")}
              className={`flex-1 py-2 text-xs rounded-md transition ${filterType === "monthWise" ? "bg-white shadow text-blue-600 font-bold" : "text-gray-500"}`}
            >
              Trend
            </button>
            <button
              onClick={() => setFilterType("oneMonth")}
              className={`flex-1 py-2 text-xs rounded-md transition ${filterType === "oneMonth" ? "bg-white shadow text-blue-600 font-bold" : "text-gray-500"}`}
            >
              Monthly
            </button>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            {filterType === "oneMonth" ? "Select Month" : "Start Date"}
          </label>
          <input
            type="date"
            value={startDate}
            max={today}
            onChange={handleStartDateChange}
            className="w-full bg-gray-50 rounded-lg text-sm px-3 py-2"
          />
        </div>

        <div
          className={
            filterType === "oneMonth" ? "opacity-40 pointer-events-none" : ""
          }
        >
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={today}
            onChange={handleEndDateChange}
            disabled={filterType === "oneMonth"}
            className="w-full bg-gray-50 rounded-lg text-sm px-3 py-2"
          />
        </div>

        {/* Forecast Split Button */}
        <div className="flex justify-end mt-4">
          <div className="inline-flex rounded-lg overflow-hidden border border-blue-500 h-9 w-full max-w-md">
            {/* Left: Action Button (WIDE) */}
            <button
              onClick={handleForecastReport}
              className="flex-1 bg-primary hover:bg-blue-600 
                 text-white text-sm font-semibold 
                 transition-all px-1 text-left"
            >
              Forecast Report
            </button>

            {/* Right: Dropdown (SMALL) */}
            <select
              value={selectForecastMonth}
              onChange={(e) => setSelectForecastMonth(Number(e.target.value))}
              className="w-28 bg-gray-50 hover:bg-gray-100 
                 text-black text-sm font-semibold 
                 border-l border-gray-200 focus:outline-none cursor-pointer px-2"
            >
              <option value="" disabled>
                Select M
              </option>
              {[0,1, 2, 3, 4, 5, 6, 12].map((m) => (
                <option key={m} value={m}>
                  {m} Month
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto min-h-[400px]">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 font-medium">Updating data...</p>
          </div>
        ) : reportData ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <ProfitLossChart data={reportData} type={filterType} />

            <DataBreakdown data={reportData} />
            <div className="mt-4">
                 <ForecastChart
              forecastData={forecastData}
              months={selectForecastMonth}
              setMonths={setSelectForecastMonth}
            />

              </div>
           
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-100 text-gray-400">
            No data found for the selected criteria.
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden">
            <div className="px-6 py-1 border-b flex justify-between items-center bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">
                New P&L Upload
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-2xl text-gray-400"
              >
                &times;
              </button>
            </div>

            <div className="p-8 text-center">
              <input
                type="file"
                id="xl"
                className="hidden"
                onChange={(e) => setFile(e.target.files[0])}
              />

              <label
                htmlFor="xl"
                className="border-2 border-dashed border-gray-200 rounded-2xl p-12 block cursor-pointer hover:border-blue-400 transition mb-6"
              >
                <p className="text-gray-500 font-medium">
                  {file ? `📄 ${file.name}` : "Click to select Excel File"}
                </p>
              </label>

              <button
                onClick={async () => {
                  if (!file) return;

                  try {
                    setLoading(true);

                    // 1️⃣ Parse Excel
                    const parsed = await parseExcel(file, {
                      companyAddressUntil: "Profit & Loss A/c",
                    });

                    // 2️⃣ Upload directly
                    await plApi.createProfiteAndLoss(
                      { ...parsed, accountId: selectedAccountId },
                      selectedAccountId,
                      token,
                    );

                    toast.success("Uploaded successfully!");
                    handleUploadSuccess();
                  } catch (err) {
                    console.error(err);
                    toast.error("Upload failed. Please check the file.");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={!file || loading}
                className="w-full bg-primary text-white py-3 rounded-xl font-bold disabled:bg-gray-300"
              >
                {loading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitAndLoss;
